import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { WaButton, scrollToId, EASE } from "./ui";

const LINES = [
  <>Arti</>,
  <>i <em className="text-gold-grad not-italic font-display italic">gurit</em>,</>,
  <>i punuar me masë.</>,
];

const SlabCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    mx.set(x - 0.5);
    my.set(y - 0.5);
    e.currentTarget.style.setProperty("--sx", `${x * 100}%`);
    e.currentTarget.style.setProperty("--sy", `${y * 100}%`);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-slab">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative aspect-[4/5] w-full rounded-[2px] slab-shadow">
        <img src="/img/calacatta.jpg" alt="Pllakë mermeri Calacatta" className="absolute inset-0 w-full h-full object-cover rounded-[2px]" />
        <div className="absolute inset-0 slab-spot rounded-[2px]" />
        <div className="absolute inset-3 border border-white/40 rounded-[1px] pointer-events-none" style={{ transform: "translateZ(40px)" }} />
        <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between text-[#1B1A17]" style={{ transform: "translateZ(70px)" }}>
          <div>
            <p className="font-mono-x text-[11px] tracking-[0.3em] uppercase">Pllaka Nr. 017</p>
            <p className="font-display text-3xl sm:text-4xl leading-none mt-1">Calacatta</p>
          </div>
          <p className="font-mono-x text-[11px] tracking-[0.2em] uppercase text-right">Me shkëlqim<br />2 · 3 cm</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const slabY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden bg-[#0B0B0C]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img src="/img/marquinia.jpg" alt="" className="w-full h-full object-cover opacity-35" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-[#0B0B0C]/40 to-[#0B0B0C]" />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end min-h-[100svh]">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} className="font-mono-x text-xs tracking-[0.35em] uppercase text-[#C9A55C] mb-8 flex items-center gap-3">
            <span className="h-px w-10 bg-[#C9A55C]" /> Shitje & përpunim · Mermer dhe granit
          </motion.p>
          <h1 className="font-display font-light text-[#F3EEE4] text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[6.2rem] xl:text-[7rem] tracking-[-0.02em]" data-testid="hero-title">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span className="block" initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }} transition={{ duration: 1.3, delay: 0.3 + i * 0.14, ease: EASE }}>
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1, ease: EASE }}>
            <p className="mt-10 max-w-xl text-base md:text-lg text-[#CFC8BA] leading-relaxed">
              Pllaka mermeri dhe graniti të përzgjedhura me dorë dhe të përpunuara në punëtorinë tonë: banakë kuzhine, banjo, shkallë dhe dysheme, nga matja deri te montimi.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <WaButton testId="hero-whatsapp-cta" topic="një projekt me porosi" />
              <button onClick={() => scrollToId("materiali")} data-testid="hero-catalog-cta" className="btn-ghost group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm tracking-wide text-[#F3EEE4]">
                Shiko materialet <ArrowDownRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
              </button>
            </div>
          </motion.div>
        </div>
        <motion.div style={{ y: slabY }} className="lg:col-span-4">
          <motion.div initial={{ opacity: 0, clipPath: "inset(100% 0% 0% 0%)" }} animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 1.6, delay: 0.6, ease: EASE }} className="max-w-[440px] mx-auto lg:ml-auto" style={{ overflow: "visible" }}>
            <SlabCard />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
